<template>
    <div>
        <div class="content-header row">
            <div class="content-header-left col-md-6 col-12 mb-2">
                <h3 class="content-header-title mb-0">Sales Reports</h3>
                <div class="row breadcrumbs-top">
                    <div class="breadcrumb-wrapper col-12">
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item"><a href="/">Home</a></li>
                            <li class="breadcrumb-item"><a href="/">Reports</a></li>
                            <li class="breadcrumb-item active">Sales Reports</li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>

        <div class="content-body">
            <section class="card">
                <div class="card-header">
                    <h4>Sales Analytics</h4>
                </div>
                <form @submit.prevent="fetchSalesAnalytics">
                    <div class="card-body">
                        <div class="row">
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>Start date</label>
                                <date-time-field v-model="filter.start_date" class="form-control" bound="start"></date-time-field>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>End date</label>
                                <date-time-field v-model="filter.end_date" class="form-control" bound="end"></date-time-field>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>State</label>
                                <select v-model="filter.state_id" class="form-control" @change="onStateChange">
                                    <option value="">All states</option>
                                    <option v-for="state in states" :key="`state-${state.id}`" :value="state.id">
                                        {{ state.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>City</label>
                                <select v-model="filter.city_id" class="form-control" @change="onCityChange">
                                    <option value="">All cities</option>
                                    <option v-for="city in filteredCities" :key="`city-${city.id}`" :value="city.id">
                                        {{ city.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Store</label>
                                <select v-model="filter.store_id" class="form-control">
                                    <option value="">All stores</option>
                                    <option v-for="store in filteredStores" :key="`store-${store.id}`" :value="store.id">
                                        {{ store.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Payment Channel</label>
                                <select v-model="filter.payment_channel" class="form-control">
                                    <option value="">All channels</option>
                                    <option v-for="channel in paymentChannels" :key="channel" :value="channel">
                                        {{ channel }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Payment Status</label>
                                <select v-model="filter.payment_status" class="form-control">
                                    <option value="">All</option>
                                    <option v-for="status in paymentStatuses" :key="status" :value="status">
                                        {{ status }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Order Channel</label>
                                <select v-model="filter.ordered_from" class="form-control">
                                    <option value="">All channels</option>
                                    <option v-for="(value, name) in platforms" :key="value" :value="value">
                                        {{ name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Product Category</label>
                                <select v-model="filter.category_id" class="form-control">
                                    <option value="">All categories</option>
                                    <option v-for="category in categories" :key="`category-${category.id}`" :value="category.id">
                                        {{ category.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Product</label>
                                <select v-model="filter.product_id" class="form-control">
                                    <option value="">All products</option>
                                    <option v-for="product in filteredProducts" :key="`product-${product.id}`" :value="product.id">
                                        {{ product.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Customer Type</label>
                                <select v-model="filter.customer_type" class="form-control">
                                    <option value="">All customers</option>
                                    <option value="new">New</option>
                                    <option value="returning">Returning</option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Gift Card</label>
                                <select v-model="filter.gift_card" class="form-control">
                                    <option value="">All</option>
                                    <option value="purchased">Purchased</option>
                                    <option value="redeemed">Redeemed</option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Transaction Medium</label>
                                <select v-model="filter.transaction_medium" class="form-control">
                                    <option value="">All</option>
                                    <option v-for="medium in transactionMediums" :key="medium" :value="medium">
                                        {{ medium }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>&nbsp;</label><br>
                                <button type="submit" class="btn btn-primary" :disabled="isLoading">Filter</button>
                                <button type="button" class="btn btn-default" @click="resetFilter">Reset</button>
                            </div>
                        </div>
                    </div>
                </form>
            </section>

            <section class="card">
                <div class="card-body">
                    <p class="text-center" v-if="analytics.date_label">
                        <b>{{ analytics.date_label }}</b>
                    </p>
                    <div class="row grouped-multiple-statistics-card">
                        <div class="col-lg-4 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">₦{{ formatMoney(analytics.total_income) }}</h3>
                            <p class="sub-heading mb-0">Total Income</p>
                        </div>
                        <div class="col-lg-4 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">{{ analytics.total_paying_customers }}</h3>
                            <p class="sub-heading mb-0">Total Paying Customers</p>
                        </div>
                        <div class="col-lg-4 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">{{ analytics.wallet_transactions_count }}</h3>
                            <p class="sub-heading mb-0">Wallet Transactions (Total Number)</p>
                        </div>
                        <div class="col-lg-4 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">₦{{ formatMoney(analytics.wallet_transactions_amount) }}</h3>
                            <p class="sub-heading mb-0">Wallet Transactions (Total Amount)</p>
                        </div>
                        <div class="col-lg-4 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">{{ analytics.gift_card_purchases_number }}</h3>
                            <p class="sub-heading mb-0">No of gift card purchases</p>
                        </div>
                        <div class="col-lg-4 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">₦{{ formatMoney(analytics.gift_card_purchases_amount) }}</h3>
                            <p class="sub-heading mb-0">Gift card total amount</p>
                        </div>
                    </div>
                </div>
            </section>

            <section class="card p-3">
                <h4 class="content-header-title mb-2">Payment Channels</h4>
                <div class="row">
                    <div class="col-md-6 mt-1">
                        <h5>Number of sales</h5>
                        <BarChart v-if="paymentChannelCountBar" :chart-data="paymentChannelCountBar"
                                  :chart-options="{responsive: true}"/>
                        <p class="text-center mb-0" v-else>No sales for the selected filters.</p>
                    </div>
                    <div class="col-md-6 mt-1">
                        <h5>Amount per channel</h5>
                        <BarChart v-if="paymentChannelAmountBar" :chart-data="paymentChannelAmountBar"
                                  :chart-options="{responsive: true}"/>
                        <p class="text-center mb-0" v-else>No sales for the selected filters.</p>
                    </div>
                </div>
            </section>

            <section class="card p-3">
                <div class="row">
                    <div class="col-md-6 mt-1">
                        <h4 class="content-header-title mb-2">Completed vs Failed Payments</h4>
                        <PieChart v-if="paymentsPie" :chart-data="paymentsPie" :chart-options="{responsive: true}"/>
                        <p class="text-center mb-0" v-else>No payments for the selected filters.</p>
                    </div>
                    <div class="col-md-6 mt-1">
                        <h4 class="content-header-title mb-2">Gift Card Redemption Rate</h4>
                        <PieChart v-if="giftCardPie" :chart-data="giftCardPie" :chart-options="{responsive: true}"/>
                        <p class="text-center mb-0" v-else>No gift cards for the selected filters.</p>
                    </div>
                </div>
            </section>

            <section class="card p-3">
                <div class="row">
                    <div class="col-md-6 mt-1">
                        <h4 class="content-header-title mb-2">Best Performing Stores (Top 10 by Revenue)</h4>
                        <BarChart v-if="bestStoresBar" :chart-data="bestStoresBar" :chart-options="{responsive: true}"/>
                        <p class="text-center mb-0" v-else>No store sales for the selected filters.</p>
                    </div>
                    <div class="col-md-6 mt-1">
                        <h4 class="content-header-title mb-2">Least Performing Stores (Last 10 by Revenue)</h4>
                        <BarChart v-if="leastStoresBar" :chart-data="leastStoresBar" :chart-options="{responsive: true}"/>
                        <p class="text-center mb-0" v-else>No store sales for the selected filters.</p>
                    </div>
                </div>
            </section>

            <section class="card p-3">
                <div class="mt-1">
                    <h4 class="content-header-title mb-2">Wallet Transactions (Credit vs Debit)</h4>
                    <BarChart v-if="walletBar" :chart-data="walletBar" :chart-options="{responsive: true}"/>
                    <p class="text-center mb-0" v-else>No wallet transactions for the selected filters.</p>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout';
import BarChart from '../../../Shared/BarChart';
import PieChart from '../../../Shared/PieChart';
import {handleErrors} from '../../../utils';

export default {
    metaInfo: {title: 'Sales Reports'},
    layout: Layout,
    name: 'SalesAnalytics',
    components: {BarChart, PieChart},
    props: [
        'states', 'cities', 'stores', 'products', 'categories',
        'platforms', 'paymentStatuses', 'paymentChannels', 'transactionMediums',
    ],
    data: () => ({
        isLoading: false,
        filter: {
            start_date: '',
            end_date: '',
            state_id: '',
            city_id: '',
            store_id: '',
            payment_channel: '',
            payment_status: '',
            ordered_from: '',
            category_id: '',
            product_id: '',
            customer_type: '',
            gift_card: '',
            transaction_medium: '',
        },
        analytics: {
            total_income: 0,
            total_paying_customers: 0,
            wallet_transactions_count: 0,
            wallet_transactions_amount: 0,
            gift_card_purchases_number: 0,
            gift_card_purchases_amount: 0,
            date_label: '',
        },
        paymentChannelCountBar: null,
        paymentChannelAmountBar: null,
        paymentsPie: null,
        giftCardPie: null,
        bestStoresBar: null,
        leastStoresBar: null,
        walletBar: null,
    }),
    computed: {
        filteredCities() {
            if (!this.filter.state_id) {
                return this.cities || [];
            }

            return (this.cities || []).filter(city => String(city.state_id) === String(this.filter.state_id));
        },
        filteredStores() {
            return (this.stores || []).filter(store => {
                if (this.filter.state_id && String(store.state_id) !== String(this.filter.state_id)) {
                    return false;
                }

                if (this.filter.city_id && String(store.city_id) !== String(this.filter.city_id)) {
                    return false;
                }

                return true;
            });
        },
        filteredProducts() {
            if (!this.filter.category_id) {
                return this.products || [];
            }

            return (this.products || []).filter(product => String(product.category_id) === String(this.filter.category_id));
        },
    },
    mounted() {
        this.fetchSalesAnalytics();
    },
    methods: {
        async fetchSalesAnalytics() {
            this.isLoading = true;
            try {
                const {data: {data}} = await axios.post(route('admin.reports.sales.analytics'), this.filter);
                this.analytics = {
                    total_income: data.total_income || 0,
                    total_paying_customers: data.total_paying_customers || 0,
                    wallet_transactions_count: data.wallet_transactions_count || 0,
                    wallet_transactions_amount: data.wallet_transactions_amount || 0,
                    gift_card_purchases_number: (data.gift_card_purchases && data.gift_card_purchases.number) || 0,
                    gift_card_purchases_amount: (data.gift_card_purchases && data.gift_card_purchases.amount) || 0,
                    date_label: data.date_label || '',
                };
                this.assignCharts(data);
            } catch (e) {
                alert(handleErrors(e.response));
            } finally {
                this.isLoading = false;
            }
        },
        assignCharts(data) {
            this.paymentChannelCountBar = this.mapToBar(data.payment_channels_count, 'Orders', '#00B5B8');
            this.paymentChannelAmountBar = this.mapToBar(data.payment_channels_amount, 'Amount (₦)', '#FF9F40');
            this.paymentsPie = this.mapToPie(data.completed_vs_failed_payments);
            this.giftCardPie = this.mapToPie(data.gift_card_redemption);
            this.bestStoresBar = this.storeBar(data.best_performing_stores || []);
            this.leastStoresBar = this.storeBar(data.least_performing_stores || []);
            this.walletBar = this.mapToBar(data.wallet_credit_vs_debit, 'Amount (₦)', '#00B5B8');
        },
        mapToBar(map, label, color) {
            const labels = Object.keys(map || {});
            if (!labels.length || labels.every(key => Number(map[key]) === 0)) {
                return null;
            }

            return {
                labels,
                datasets: [{
                    label,
                    backgroundColor: color,
                    data: labels.map(key => map[key]),
                }],
            };
        },
        mapToPie(map) {
            const labels = Object.keys(map || {});
            const values = labels.map(key => Number(map[key]) || 0);
            if (!labels.length || values.every(value => value === 0)) {
                return null;
            }

            return {
                labels,
                datasets: [{
                    backgroundColor: labels.map(label => this.chartColorFor(label)),
                    data: values,
                }],
            };
        },
        storeBar(stores) {
            if (!stores.length) {
                return null;
            }

            return {
                labels: stores.map(store => store.store_name),
                datasets: [
                    {
                        label: 'Orders',
                        backgroundColor: '#00B5B8',
                        data: stores.map(store => store.orders),
                    },
                    {
                        label: 'Income (₦)',
                        backgroundColor: '#FF9F40',
                        data: stores.map(store => store.income),
                    },
                ],
            };
        },
        chartColorFor(label) {
            const key = String(label || '').toLowerCase();
            return ['failed', 'unredeemed', 'debit'].includes(key) ? '#FF9F40' : '#00B5B8';
        },
        formatMoney(value) {
            return Number(value || 0).toLocaleString('en-NG', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        },
        defaultFilter() {
            return {
                start_date: '',
                end_date: '',
                state_id: '',
                city_id: '',
                store_id: '',
                payment_channel: '',
                payment_status: '',
                ordered_from: '',
                category_id: '',
                product_id: '',
                customer_type: '',
                gift_card: '',
                transaction_medium: '',
            };
        },
        async resetFilter() {
            this.filter = this.defaultFilter();
            await this.fetchSalesAnalytics();
        },
        onStateChange() {
            this.filter.city_id = '';
            this.filter.store_id = '';
        },
        onCityChange() {
            this.filter.store_id = '';
        },
    },
};
</script>
